import { create } from "zustand";

import { FormDataKey } from "../useFormData/formData";
import {
  signUpDripStepCompleted,
  fireLeadAndIterableEventsForEmailField,
} from "@utils/signUpDripStepCompleted";
import { useFormData } from "../useFormData";
import { Cookie } from "@utils/Cookie";
import {
  OfferAttribute,
  QuestionAttribute,
} from "@components/BuilderComponents/SignupFormDrip/sfd-components/Form/sfd-helpers/sfd-form.type";

export type FormField = {
  field: Exclude<FormDataKey, "otherProfession">;
  questionAttributes: QuestionAttribute | undefined;
  offerAttributesOne: OfferAttribute | undefined;
  offerAttributesTwo: OfferAttribute | undefined;
  defaultOfferOne: boolean | undefined;
};

type FormStep = {
  headline: string;
  subcopy: string;
  fields: FormField[];
};

export type FormSteps = FormStep[];

type UseSignupFormDripState = {
  currentStep: number;
  allowNext: boolean;
  steps: FormSteps;
  progress: (1 | null)[];
  startTime: number;
  lastStepCta: string;
};

type UseSignupFormDripActions = {
  getCurrentStep: () => FormStep | undefined;
  getIsFirstStep: () => boolean;
  getIsLastStep: () => boolean;
  next: () => void;
  prev: () => void;
  reset: () => void;
  setAllowNext: () => void;
  getPromoCode: () => string | null;
  setSteps: (steps: UseSignupFormDripState["steps"]) => void;
  setLastStepCta: (ctaLastStep?: string) => void;
};

const initialState: UseSignupFormDripState = {
  currentStep: 0,
  allowNext: false,
  steps: [],
  progress: [],
  startTime: Date.now(),
  lastStepCta: "",
};

/**
* ### Store Methods
* - `getCurrentStep`  -> returns current step data
* - `getIsFirstStep` -> returns a boolean
* - `getIsLastStep` -> returns a boolean
* - `next` -> on click, increases currentStep value, and and updates progress data
* - `prev` -> on click, decreases currentStep value, and updates progress data
* - `reset` -> reset state to initial state
* - `setAllowNext` -> handles logic to allow users to continue to the next step
* - `setSteps` -> handles logic for storing all form steps, and progress data

* 💡 `progress` is an array that contains `1` or `null`, **e.g** `[1, null, null, null]`. Each null value represents the inactive state in the ui progress bar and the 1 represent active state. `next / prev` methods will handle the logic to update the values within the array to reflect ui changes.
 */
export const useSignupFormDrip = create<
  UseSignupFormDripState & UseSignupFormDripActions
>((set, get) => ({
  ...initialState,
  getCurrentStep: () => {
    const { currentStep, steps } = get();

    return steps[currentStep];
  },
  getPromoCode: () => {
    return Cookie.checkCookie("PROMOCODE")
      ? Cookie.getCookie("PROMOCODE")
      : null;
  },
  getIsFirstStep: () => {
    const { currentStep } = get();

    return currentStep === 0;
  },
  getIsLastStep: () => {
    const { currentStep, steps } = get();

    return currentStep === steps.length - 1;
  },
  next: () => {
    set(state => {
      if (state.currentStep >= state.steps.length - 1) {
        return {
          currentStep: state.currentStep,
        };
      }

      const formData = useFormData.getState();
      const email = formData["email"];
      fireLeadAndIterableEventsForEmailField({
        fields: state.steps[state.currentStep].fields,
        email,
      });

      signUpDripStepCompleted({
        fields: state.steps[state.currentStep].fields,
        step: state.currentStep + 1,
      });

      const currentStep = state.currentStep + 1;
      const progress = [...state.progress];

      progress[currentStep] = 1;
      return {
        allowNext: false,
        currentStep,
        progress,
      };
    });
  },
  prev: () => {
    set(state => {
      if (state.currentStep <= 0) {
        return {
          currentStep: state.currentStep,
        };
      }

      const currentStep = state.currentStep - 1;
      const progress = [...state.progress];
      progress[state.currentStep] = null;

      return {
        allowNext: true,
        currentStep,
        progress,
      };
    });
  },
  reset: () => set(initialState),
  setAllowNext: () => {
    const { getCurrentStep } = get();
    const formData = useFormData.getState();
    const step = getCurrentStep();

    const allowNext = step?.fields.every(item => {
      const currentField = formData[item?.field];

      if (item?.field === "questions") {
        const question =
          formData?.questions?.[item?.questionAttributes?.questionName || ""];
        if (question?.questionOptional) return true;

        if (question?.hasError || !question?.isValid || !question?.value) {
          return false;
        }

        return true;
      }

      if (item?.field === "offer") {
        return true;
      }

      if (
        "value" in currentField &&
        currentField.value &&
        typeof currentField.value === "string" &&
        currentField.value.trim() !== "" &&
        !currentField.hasError
      ) {
        return true;
      } else if (
        "checked" in currentField &&
        currentField.checked &&
        currentField.isValid &&
        !currentField.hasError
      ) {
        return true;
      }

      return false;
    });

    set({ allowNext });
  },
  setSteps: steps => {
    const progress = steps.map((_, idx) => {
      if (idx === 0) return 1;

      return null;
    });

    set({ steps, progress });
  },
  setLastStepCta: (ctaLastStep?: string) => {
    if (ctaLastStep) {
      set({ lastStepCta: ctaLastStep });
    } else {
      set({ lastStepCta: "Start My Free Trial Now" });
    }
  },
}));
